import React, { useState, useEffect } from 'react';
import { Inbox, FileText, HelpCircle, Clock, CheckCircle, AlertCircle, Search, Loader2 } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { createGrievance } from '../services/api';
import { DEFAULT_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';

const GrievancePage = () => {
  const [activeTab, setActiveTab] = useState('submit');
  const [selectedCategory, setSelectedCategory] = useState('Labour');
  const [grievances, setGrievances] = useState([]);
  const [trackTicketNumber, setTrackTicketNumber] = useState('');
  const [trackEmail, setTrackEmail] = useState('');
  const [tracking, setTracking] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [submitting, setSubmitting] = useState(false);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    // Listen for custom language change events
    window.addEventListener('languageChanged', handleLanguageChange);
    
    // Listen for storage changes (for cross-tab synchronization)
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const categories = [
    'Labour', 'Safety', 'Pollution', 'Land', 'Electricity', 
    'Water', 'Roads', 'Waste', 'Pricing', 'Other'
  ];

  const priorities = ['Low', 'Medium', 'High', 'Urgent', 'Critical'];

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackTicketNumber.trim() || !trackEmail.trim()) {
      toast.error(
        getText('grievance.track.validation.missingFields') ||
          'Please enter both Ticket Number and Email used while submitting grievance.'
      );
      return;
    }
    setTracking(true);
    try {
      const baseUrl = DEFAULT_BASE_URL.replace(/\/$/, '');
      const params = new URLSearchParams({
        ticketNumber: trackTicketNumber.trim(),
        email: trackEmail.trim()
      });
      const res = await fetch(`${baseUrl}/grievances/track?${params.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(
          data.message ||
            getText('grievance.track.notFound') ||
            'Unable to find grievance for provided details.'
        );
      }
      const item = {
        id: data.ticketNumber,
        ticketNumber: data.ticketNumber,
        subject: data.title,
        category: data.category,
        priority: data.priority,
        status: data.status,
        submittedAt: data.createdAt ? new Date(data.createdAt).toLocaleDateString('en-IN') : '',
        description: data.description,
        resolution: data.resolution,
        updatedAt: data.updatedAt
      };
      setGrievances([item]);
      toast.success(
        getText('grievance.track.success') || 'Grievance details fetched successfully.'
      );
    } catch (error) {
      toast.error(
        error.message ||
          getText('grievance.track.error') ||
          'Failed to fetch grievance details. Please check details and try again.'
      );
      setGrievances([]);
    } finally {
      setTracking(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: 'Labour',
    priority: 'Medium',
    subject: '',
    description: '',
    isUrgent: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name.trim()) {
      toast.error(getText('grievance.form.name') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!formData.email.trim()) {
      toast.error(getText('grievance.form.email') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!formData.phone.trim()) {
      toast.error(getText('grievance.form.phone') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!formData.subject.trim()) {
      toast.error(getText('grievance.form.subjectLabel') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!formData.description.trim()) {
      toast.error(getText('grievance.form.descriptionLabel') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    
    setSubmitting(true);
    try {
      await createGrievance({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        category: formData.category,
        priority: formData.priority,
        subject: formData.subject,
        description: formData.description,
        isUrgent: formData.isUrgent
      });
      toast.success(getText('grievance.form.success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        category: 'Labour',
        priority: 'Medium',
        subject: '',
        description: '',
        isUrgent: false
      });
      setActiveTab('track');
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message || getText('grievance.form.error');
      toast.error(errorMessage);
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
        console.error(error);
      }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-blue-100 text-blue-800';
      case 'High': return 'bg-yellow-100 text-yellow-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Helpdesk & Grievances
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              Submit your concerns, track resolution progress, and get expert support for all industrial matters.
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'submit', nameKey: 'grievance.tab.submit', icon: Inbox },
              { id: 'track', nameKey: 'grievance.tab.track', icon: FileText },
              { id: 'faq', nameKey: 'grievance.tab.faq', icon: HelpCircle }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {getText(tab.nameKey)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Submit Grievance Tab */}
          {activeTab === 'submit' && (
            <div className="max-w-2xl mx-auto">
              <div className="card">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{getText('grievance.form.title')}</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('grievance.form.name')} *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="input-field"
                        placeholder={getText('grievance.form.name')}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('grievance.form.email')} *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="input-field"
                        placeholder={getText('grievance.form.email')}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('grievance.form.phone')} *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="input-field"
                        placeholder={getText('grievance.form.phone')}
                        maxLength={15}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('grievance.form.company')}
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="input-field"
                        placeholder={getText('grievance.form.company')}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('grievance.form.category')} *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="input-field"
                        required
                      >
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('grievance.form.priority')} *
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({...formData, priority: e.target.value})}
                        className="input-field"
                        required
                      >
                        {priorities.map(priority => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getText('grievance.form.subjectLabel')} *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="input-field"
                      placeholder={getText('contact.form.subject')}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getText('grievance.form.descriptionLabel')} *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="input-field"
                      rows={5}
                      placeholder={getText('grievance.form.description')}
                      required
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="urgent"
                      checked={formData.isUrgent}
                      onChange={(e) => setFormData({...formData, isUrgent: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="urgent" className="ml-2 block text-sm text-gray-900">
                      {getText('grievance.form.urgentLabel')}
                    </label>
                  </div>
                  
                  <div className="pt-4">
                    <button 
                      type="submit" 
                      disabled={submitting}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {getText('grievance.form.submitting') || 'Submitting...'}
                        </>
                      ) : (
                        getText('grievance.form.submit')
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Track Grievances Tab */}
          {activeTab === 'track' && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{getText('grievance.track.title')}</h2>
                <form onSubmit={handleTrack} className="grid md:grid-cols-3 gap-4 items-end">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ticket Number *
                    </label>
                    <input
                      type="text"
                      value={trackTicketNumber}
                      onChange={(e) => setTrackTicketNumber(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g. IFM-GR-20260001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email used while submitting *
                    </label>
                    <input
                      type="email"
                      value={trackEmail}
                      onChange={(e) => setTrackEmail(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={tracking}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                    >
                      {tracking ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Track Grievance
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              <div className="space-y-4">
                {grievances.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{getText('grievance.track.noGrievances')}</h3>
                    <p className="text-gray-500">{getText('grievance.track.noGrievancesDesc')}</p>
                  </div>
                ) : (
                  grievances.map((grievance) => (
                    <div key={grievance.id} className="card">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <span className="text-lg font-semibold text-gray-900">
                              {grievance.ticketNumber}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(grievance.status)}`}>
                              {grievance.status}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(grievance.priority)}`}>
                              {grievance.priority}
                            </span>
                          </div>
                          
                          <h3 className="text-lg font-medium text-gray-900 mb-2">{grievance.subject}</h3>
                          <p className="text-gray-600 mb-3">{grievance.description}</p>
                          
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-1" />
                            {getText('grievance.track.submittedOn')} {grievance.submittedAt}
                          </div>
                          {grievance.resolution && (
                            <div className="mt-3 text-sm text-gray-600">
                              <span className="font-semibold">Resolution:</span> {grievance.resolution}
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-4">
                          <button className="btn-secondary text-sm">
                            {getText('grievance.track.viewDetails')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">{getText('grievance.faq.title')}</h2>
              
              <div className="space-y-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{getText('grievance.faq.q1')}</h3>
                  <p className="text-gray-600">
                    {getText('grievance.faq.a1')}
                  </p>
                </div>
                
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{getText('grievance.faq.q2')}</h3>
                  <p className="text-gray-600">
                    {getText('grievance.faq.a2')}
                  </p>
                </div>
                
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{getText('grievance.faq.q3')}</h3>
                  <p className="text-gray-600">
                    {getText('grievance.faq.a3')}
                  </p>
                </div>
                
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{getText('grievance.faq.q4')}</h3>
                  <p className="text-gray-600">
                    {getText('grievance.faq.a4')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default GrievancePage;
